import { weekLabel, weekYear, PHASE_LABELS } from "@/lib/format";

const stripGreeting = (s = "") => s.replace(/^(hei|hallo|god dag|heisann)[\s!,.]*/i, "").trim();

/** Short, honest excerpt of the customer's own words (no AI summary). */
export function messageExcerpt(m, max = 72) {
  if (!m) return "";
  const raw = stripGreeting(m.excerpt || m.body || "").replace(/\s+/g, " ");
  const t = raw.charAt(0).toUpperCase() + raw.slice(1);
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 40))}…`;
}

/**
 * A readable task title for a customer message: the registered headline when we have one,
 * otherwise a meaningful subject, otherwise the customer's own words. Never a truncated e-mail.
 */
export function messageHeadline(m) {
  if (!m) return "Kundebeskjed";
  if (m.headline) return m.headline;
  const subj = (m.subject || "").trim();
  if (subj && !/^(re|sv|fwd|vs):/i.test(subj) && !/^ordrebekreftelse/i.test(subj)) return subj;
  return messageExcerpt(m);
}

/* Concrete verbs for actions. Waiting states keep their label and get no verb. */
const VERB = {
  melding: "Vurder beskjed",
  endring: "Fortsett endring",
  frigivelse: "Start kontroll",
  godkjenning: "Send til godkjenning",
  plan: "Planlegg produksjon",
  service: "Se restpunkter",
  spesifikasjon: "Åpne forespørsel",
  dokumenter: "Åpne leveranse",
  montering: "Åpne montering",
  oversikt: "Åpne leveranse",
};

const noSpec = (s) => ["foresporsel", "tilbud"].includes(s.phase);

/** Something the user can do here (as opposed to waiting on customer/others). */
export const isActionable = (fu) => !!fu && (fu.kind === "handling" || fu.kind === "blokker");

/**
 * Derive the follow-up row for a delivery summary: what it concerns and the concrete action.
 * `kind`: handling (needs someone here), venter (waiting on others), blokker, info.
 */
export function followUp(s) {
  const na = s.next_action || { label: "", kind: "info", target: "oversikt" };
  const m = s.latest_unhandled_message;
  if (m) {
    const title = messageHeadline(m);
    const verb = noSpec(s) ? "Vurder forespørsel" : "Vurder beskjed";
    return { title, detail: m.sender ? `${m.sender}` : null, kind: "handling", verb, target: "melding", ref: m.id };
  }
  const actionable = na.kind === "handling" || na.kind === "blokker";
  let verb = actionable ? VERB[na.target] || "Åpne leveranse" : null;
  let title = na.label;
  if (na.target === "frigivelse") title = "Klar til intern kontroll";
  if (na.target === "melding" && actionable) title = "Ny kundebeskjed";
  if (/^avtal oppmåling/i.test(na.label)) { verb = "Avtal oppmåling"; title = "Oppmåling mangler"; }
  if (na.target === "spesifikasjon" && s.phase === "foresporsel") { verb = "Avklar og lag tilbud"; title = "Henvendelse må avklares"; }
  if (na.target === "service" && actionable) title = na.label.replace(/^(\d+) åpne restpunkt(er)?$/, "$1 åpne restpunkt$2 etter levering");
  return { title, detail: null, kind: na.kind, verb, target: na.target, ref: na.ref };
}

/** Short, readable reason for a plan follow-up – the cause in words, never an abbreviation. */
export function readableReason(text = "") {
  const t = text.trim();
  if (!t) return "";
  if (/^mangler intern frigivelse$/i.test(t)) return "Intern frigivelse mangler";
  const m = t.match(/^Rev\. (\w+) mangler intern frigivelse$/i);
  if (m) return `Rev. ${m[1]}: intern frigivelse mangler`;
  if (/^ikke tildelt$/i.test(t)) return "Montør ikke tildelt";
  if (/^spesifikasjon må sendes til kundegodkjenning$/i.test(t)) return "Spesifikasjon ikke sendt til godkjenning";
  if (/^tegning må sendes til kundegodkjenning$/i.test(t)) return "Tegning ikke sendt til godkjenning";
  return t;
}

/** Where the action takes the user. A customer message always opens the same work surface. */
export function followUpHref(s) {
  const na = s.next_action || {};
  const m = s.latest_unhandled_message;
  if (m) return noSpec(s) ? `/leveranser/${s.id}` : `/leveranser/${s.id}?melding=${m.id}`;
  switch (na.target) {
    case "melding": return `/leveranser/${s.id}?melding=${na.ref}`;
    case "endring": return `/leveranser/${s.id}/endring/${na.ref}`;
    case "frigivelse": return `/leveranser/${s.id}?handling=kontroll`;
    case "godkjenning": return na.kind === "venter" ? `/leveranser/${s.id}` : `/leveranser/${s.id}?handling=godkjenning`;
    case "plan": return "/plan";
    case "service": return `/service?leveranse=${s.id}`;
    case "spesifikasjon": return `/leveranser/${s.id}?tab=spesifikasjon`;
    case "dokumenter": return `/leveranser/${s.id}?tab=dokumenter`;
    case "montering": return `/leveranser/${s.id}?tab=montering`;
    default: return `/leveranser/${s.id}`;
  }
}

/** Week label with the year when it is otherwise ambiguous (other year than today's). */
export function weekText(week) {
  if (!week) return "–";
  const y = weekYear(week);
  const now = String(new Date().getFullYear());
  return y && y !== now ? `${weekLabel(week)} · ${y}` : weekLabel(week);
}

/** "Avtalt levering uke 47" / "Ønsket levering uke 50" / "Ønsket: i løpet av mai". */
export function deliveryText(dates = {}) {
  if (dates.agreed_delivery_week) return { main: weekText(dates.agreed_delivery_week), sub: "avtalt", full: `Avtalt levering ${weekText(dates.agreed_delivery_week).toLowerCase()}` };
  if (dates.desired_week) return { main: weekText(dates.desired_week), sub: "ønsket", full: `Ønsket levering ${weekText(dates.desired_week).toLowerCase()}` };
  if (dates.desired_text) return { main: dates.desired_text, sub: "ønsket", full: `Ønsket: ${dates.desired_text}` };
  return { main: "Ikke avtalt", sub: null, full: "Leveringsuke ikke avtalt" };
}

/** "Klargjøring · Avtalt levering uke 47" – the one-line context under a task. */
export function contextLine(s) {
  const del = deliveryText(s.dates);
  return [PHASE_LABELS[s.phase] || s.phase_label, del.full].filter(Boolean).join(" · ");
}

/** "Prosjekt Furulund · Klargjøring · Avtalt levering uke 47" – project as context under a task title. */
export function taskContext(s) {
  const { name, unit } = splitProjectName(s);
  return [name, unit, PHASE_LABELS[s.phase] || s.phase_label, deliveryText(s.dates).full].filter(Boolean).join(" · ");
}

/**
 * Split the project name into a primary name and a unit line for the register.
 * "Bakkehaugen Panorama – Bygg A, leil. 1" → { name: "Bakkehaugen Panorama", unit: "Bygg A · Leil. 1" }
 */
export function splitProjectName(s) {
  const raw = s.project_name || "";
  const parts = raw.split(/\s+[–-]\s+/);
  if (parts.length >= 2 && (s.project_group || /bygg|leil|hus\s*\d|seksjon|oppgang/i.test(parts[1]))) {
    const unit = parts.slice(1).join(" · ").replace(/,\s*/g, " · ").replace(/\bleil\./gi, "Leil.").replace(/\bbygg\b/gi, "Bygg");
    return { name: parts[0], unit };
  }
  return { name: raw, unit: null };
}
